import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, CalendarHeart, Users, Flame, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../lib/auth-context';
import { Activity, ActivityCategory, ActivityDifficulty, ActivityBudget } from '../types/activities';
import { ActivityFilters, ActivityFiltersState } from '../components/activities/ActivityFilters';
import { ActivityList } from '../components/activities/ActivityList';
import { ActivityDetailModal } from '../components/activities/ActivityDetailModal';
import { CreateActivityForm } from '../components/activities/CreateActivityForm';
import {
  collection, query, where, getDocs, orderBy, limit, startAfter,
  doc, updateDoc, arrayUnion, arrayRemove, addDoc, deleteDoc, Timestamp, getDoc
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { createActivityNotification } from '../lib/notification-service';
import * as amplitude from '@amplitude/analytics-browser';
import { markSectionVisited } from '../hooks/useNewItems';
import { seedActivities } from '../utils/seed-activities';

const ACTIVITIES_PER_PAGE = 12;

export function ActivitiesPage() {
  const { user, userInfo, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [activities, setActivities] = React.useState<Activity[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showCreateForm, setShowCreateForm] = React.useState(false);
  const [selectedActivity, setSelectedActivity] = React.useState<Activity | null>(null);
  const [currentFilters, setCurrentFilters] = React.useState<ActivityFiltersState>({});
  const [lastVisible, setLastVisible] = React.useState<any>(null);
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isJoining, setIsJoining] = React.useState(false);
  const [totalCount, setTotalCount] = React.useState(0);
  const [likedIds, setLikedIds] = React.useState<Set<string>>(new Set());
  const [favoritedIds, setFavoritedIds] = React.useState<Set<string>>(new Set());
  const observerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    amplitude.track('Activities Page Viewed');
    markSectionVisited('activities');
  }, [user, navigate]);

  React.useEffect(() => {
    if (activities.length > 0 && user) {
      const liked = new Set<string>();
      const favorited = new Set<string>();
      activities.forEach(a => {
        if (a.likes?.includes(user.uid)) liked.add(a.id);
        if (a.favorites?.includes(user.uid)) favorited.add(a.id);
      });
      setLikedIds(liked);
      setFavoritedIds(favorited);
    }
  }, [activities, user]);

  const fetchActivities = React.useCallback(async (filters: ActivityFiltersState = {}, append = false) => {
    try {
      if (!append) setIsLoading(true);
      else setIsLoadingMore(true);

      const activitiesRef = collection(db, 'activities');
      const constraints: any[] = [
        where('isActive', '==', true),
        where('status', '==', 'open'),
      ];

      if (filters.category) {
        constraints.push(where('category', '==', filters.category));
      }
      if (filters.countryCode) {
        constraints.push(where('countryCode', '==', filters.countryCode));
      }

      constraints.push(orderBy('date', 'asc'));
      constraints.push(limit(ACTIVITIES_PER_PAGE));

      if (append && lastVisible) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(activitiesRef, ...constraints);
      const snapshot = await getDocs(q);

      let results = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
      })) as Activity[];

      if (filters.city) {
        const cityLower = filters.city.toLowerCase();
        results = results.filter(a =>
          a.city.toLowerCase().includes(cityLower)
        );
      }

      if (append) {
        setActivities(prev => [...prev, ...results]);
      } else {
        setActivities(results);
      }

      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === ACTIVITIES_PER_PAGE);
      setTotalCount(prev => append ? prev : results.length);
    } catch (error) {
      console.error('Error fetching activities:', error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [lastVisible]);

  React.useEffect(() => {
    fetchActivities(currentFilters);
  }, []);

  const handleFilterChange = (filters: ActivityFiltersState) => {
    setCurrentFilters(filters);
    setLastVisible(null);
    setHasMore(true);
    fetchActivities(filters, false);
  };

  React.useEffect(() => {
    if (!hasMore || isLoadingMore || isLoading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          fetchActivities(currentFilters, true);
        }
      },
      { threshold: 0.1 }
    );
    if (observerRef.current) observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, isLoading, currentFilters, fetchActivities]);

  const handleCreateActivity = async (data: {
    title: string;
    description: string;
    category: ActivityCategory;
    date: string;
    time: string;
    city: string;
    country: string;
    countryCode: string;
    meetingPoint: string;
    maxParticipants: number;
    difficulty: ActivityDifficulty;
    budget: ActivityBudget;
    spokenLanguages: string[];
    requirements: string;
  }) => {
    if (!user || !userInfo) return;

    setIsSubmitting(true);
    try {
      const activityDate = new Date(data.date);
      const newActivity = {
        userId: user.uid,
        username: userInfo.user.username,
        userAvatar: userInfo.user.avatar?.url || '',
        title: data.title,
        description: data.description,
        category: data.category,
        date: Timestamp.fromDate(activityDate),
        time: data.time,
        city: data.city,
        country: data.country,
        countryCode: data.countryCode,
        meetingPoint: data.meetingPoint || '',
        maxParticipants: data.maxParticipants,
        difficulty: data.difficulty,
        budget: data.budget,
        spokenLanguages: data.spokenLanguages,
        requirements: data.requirements || '',
        participants: [{
          userId: user.uid,
          username: userInfo.user.username,
          avatar: userInfo.user.avatar?.url || '',
          joinedAt: Timestamp.now(),
        }],
        comments: [],
        likes: [],
        favorites: [],
        status: 'open',
        isActive: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, 'activities'), newActivity);
      const created = { id: docRef.id, ...newActivity } as Activity;
      setActivities(prev => [created, ...prev]);
      setShowCreateForm(false);
      setTotalCount(prev => prev + 1);

      amplitude.track('Activity Created', { category: data.category, city: data.city });
    } catch (error) {
      console.error('Error creating activity:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLike = async (activityId: string) => {
    if (!user) return;
    try {
      const activityRef = doc(db, 'activities', activityId);
      const isCurrentlyLiked = likedIds.has(activityId);

      if (isCurrentlyLiked) {
        await updateDoc(activityRef, { likes: arrayRemove(user.uid) });
        setLikedIds(prev => { const s = new Set(prev); s.delete(activityId); return s; });
        setActivities(prev => prev.map(a =>
          a.id === activityId ? { ...a, likes: (a.likes || []).filter(id => id !== user.uid) } : a
        ));
        if (selectedActivity?.id === activityId) {
          setSelectedActivity(prev => prev ? { ...prev, likes: (prev.likes || []).filter(id => id !== user.uid) } : null);
        }
      } else {
        await updateDoc(activityRef, { likes: arrayUnion(user.uid) });
        setLikedIds(prev => new Set(prev).add(activityId));
        setActivities(prev => prev.map(a =>
          a.id === activityId ? { ...a, likes: [...(a.likes || []), user.uid] } : a
        ));
        if (selectedActivity?.id === activityId) {
          setSelectedActivity(prev => prev ? { ...prev, likes: [...(prev.likes || []), user.uid] } : null);
        }
        const activity = activities.find(a => a.id === activityId);
        if (activity && activity.userId !== user.uid) {
          createActivityNotification({
            recipientId: activity.userId,
            senderId: user.uid,
            senderUsername: userInfo?.user.username || '',
            type: 'activity_like',
            activityId,
            activityTitle: activity.title,
          });
        }
      }
      amplitude.track(isCurrentlyLiked ? 'Activity Unliked' : 'Activity Liked', { activityId });
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleFavorite = async (activityId: string) => {
    if (!user) return;
    try {
      const activityRef = doc(db, 'activities', activityId);
      const isCurrentlyFavorited = favoritedIds.has(activityId);

      if (isCurrentlyFavorited) {
        await updateDoc(activityRef, { favorites: arrayRemove(user.uid) });
        setFavoritedIds(prev => { const s = new Set(prev); s.delete(activityId); return s; });
        setActivities(prev => prev.map(a =>
          a.id === activityId ? { ...a, favorites: (a.favorites || []).filter(id => id !== user.uid) } : a
        ));
        if (selectedActivity?.id === activityId) {
          setSelectedActivity(prev => prev ? { ...prev, favorites: (prev.favorites || []).filter(id => id !== user.uid) } : null);
        }
      } else {
        await updateDoc(activityRef, { favorites: arrayUnion(user.uid) });
        setFavoritedIds(prev => new Set(prev).add(activityId));
        setActivities(prev => prev.map(a =>
          a.id === activityId ? { ...a, favorites: [...(a.favorites || []), user.uid] } : a
        ));
        if (selectedActivity?.id === activityId) {
          setSelectedActivity(prev => prev ? { ...prev, favorites: [...(prev.favorites || []), user.uid] } : null);
        }
      }
      amplitude.track(isCurrentlyFavorited ? 'Activity Unfavorited' : 'Activity Favorited', { activityId });
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleJoin = async (activityId: string) => {
    if (!user || !userInfo) return;
    setIsJoining(true);
    try {
      const activityRef = doc(db, 'activities', activityId);
      const participant = {
        userId: user.uid,
        username: userInfo.user.username,
        avatar: userInfo.user.avatar?.url || '',
        joinedAt: Timestamp.now(),
      };
      await updateDoc(activityRef, {
        participants: arrayUnion(participant),
        updatedAt: Timestamp.now(),
      });

      setActivities(prev => prev.map(a => {
        if (a.id === activityId) {
          return { ...a, participants: [...a.participants, participant] } as Activity;
        }
        return a;
      }));
      if (selectedActivity?.id === activityId) {
        setSelectedActivity(prev => prev ? { ...prev, participants: [...prev.participants, participant] } as Activity : null);
      }

      const activity = activities.find(a => a.id === activityId);
      if (activity && activity.userId !== user.uid) {
        createActivityNotification({
          recipientId: activity.userId,
          senderId: user.uid,
          senderUsername: userInfo.user.username,
          type: 'activity_join',
          activityId,
          activityTitle: activity.title,
        });
      }

      amplitude.track('Activity Joined', { activityId });
    } catch (error) {
      console.error('Error joining activity:', error);
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeave = async (activityId: string) => {
    if (!user) return;
    setIsJoining(true);
    try {
      const activity = activities.find(a => a.id === activityId);
      if (!activity) return;

      const currentParticipant = activity.participants.find(p => p.userId === user.uid);
      if (!currentParticipant) return;

      const activityRef = doc(db, 'activities', activityId);
      await updateDoc(activityRef, {
        participants: arrayRemove(currentParticipant),
        updatedAt: Timestamp.now(),
      });

      setActivities(prev => prev.map(a => {
        if (a.id === activityId) {
          return { ...a, participants: a.participants.filter(p => p.userId !== user.uid) } as Activity;
        }
        return a;
      }));
      if (selectedActivity?.id === activityId) {
        setSelectedActivity(prev => prev ? { ...prev, participants: prev.participants.filter(p => p.userId !== user.uid) } as Activity : null);
      }

      amplitude.track('Activity Left', { activityId });
    } catch (error) {
      console.error('Error leaving activity:', error);
    } finally {
      setIsJoining(false);
    }
  };

  const handleComment = async (activityId: string, content: string) => {
    if (!user || !userInfo) return;
    try {
      const newComment = {
        id: Date.now().toString(),
        userId: user.uid,
        username: userInfo.user.username,
        avatar: userInfo.user.avatar?.url || '',
        content,
        replies: [],
        createdAt: Timestamp.now(),
      };

      const activityRef = doc(db, 'activities', activityId);
      await updateDoc(activityRef, {
        comments: arrayUnion(newComment),
        updatedAt: Timestamp.now(),
      });

      setActivities(prev => prev.map(a => {
        if (a.id === activityId) {
          return { ...a, comments: [...a.comments, newComment] } as Activity;
        }
        return a;
      }));
      if (selectedActivity?.id === activityId) {
        setSelectedActivity(prev => prev ? { ...prev, comments: [...prev.comments, newComment] } as Activity : null);
      }

      const activity = activities.find(a => a.id === activityId);
      if (activity && activity.userId !== user.uid) {
        createActivityNotification({
          recipientId: activity.userId,
          senderId: user.uid,
          senderUsername: userInfo.user.username,
          type: 'activity_comment',
          activityId,
          activityTitle: activity.title,
        });
      }

      amplitude.track('Activity Comment Added', { activityId });
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleReply = async (activityId: string, commentId: string, content: string) => {
    if (!user || !userInfo) return;
    try {
      const activityRef = doc(db, 'activities', activityId);
      const activityDoc = await getDoc(activityRef);
      if (!activityDoc.exists()) return;

      const data = activityDoc.data();
      const comments = [...(data.comments || [])];
      const commentIndex = comments.findIndex((c: any) => c.id === commentId);
      if (commentIndex === -1) return;

      const newReply = {
        id: Date.now().toString(),
        userId: user.uid,
        username: userInfo.user.username,
        avatar: userInfo.user.avatar?.url || '',
        content,
        createdAt: Timestamp.now(),
      };

      comments[commentIndex] = {
        ...comments[commentIndex],
        replies: [...(comments[commentIndex].replies || []), newReply],
      };

      await updateDoc(activityRef, { comments, updatedAt: Timestamp.now() });

      setActivities(prev => prev.map(a => {
        if (a.id === activityId) {
          const updatedComments = a.comments.map(c => {
            if (c.id === commentId) {
              return { ...c, replies: [...(c.replies || []), newReply] };
            }
            return c;
          });
          return { ...a, comments: updatedComments } as Activity;
        }
        return a;
      }));
      if (selectedActivity?.id === activityId) {
        setSelectedActivity(prev => {
          if (!prev) return null;
          const updatedComments = prev.comments.map(c => {
            if (c.id === commentId) {
              return { ...c, replies: [...(c.replies || []), newReply] };
            }
            return c;
          });
          return { ...prev, comments: updatedComments } as Activity;
        });
      }

      const activity = activities.find(a => a.id === activityId);
      if (activity) {
        const comment = activity.comments.find(c => c.id === commentId);
        if (comment && comment.userId !== user.uid) {
          createActivityNotification({
            recipientId: comment.userId,
            senderId: user.uid,
            senderUsername: userInfo.user.username,
            type: 'activity_reply',
            activityId,
            activityTitle: activity.title,
          });
        }
        if (activity.userId !== user.uid && activity.userId !== comment?.userId) {
          createActivityNotification({
            recipientId: activity.userId,
            senderId: user.uid,
            senderUsername: userInfo.user.username,
            type: 'activity_reply',
            activityId,
            activityTitle: activity.title,
          });
        }
      }

      amplitude.track('Activity Reply Added', { activityId, commentId });
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  const handleDelete = async (activityId: string) => {
    try {
      await deleteDoc(doc(db, 'activities', activityId));
      setActivities(prev => prev.filter(a => a.id !== activityId));
      setSelectedActivity(null);
      setTotalCount(prev => prev - 1);

      amplitude.track('Activity Deleted', { activityId });
    } catch (error) {
      console.error('Error deleting activity:', error);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <div className="hidden sm:block relative w-full overflow-hidden">
        <div className="relative h-64 md:h-72 bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-800">
          <img
            src="https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=1400&h=400&fit=crop"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-overlay"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-teal-900/80 via-teal-800/40 to-transparent" />
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-white rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
          </div>

          <div className="relative max-w-7xl mx-auto w-full h-full px-4 sm:px-6 flex items-end pb-8">
            <div className="flex items-end justify-between w-full">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                    <CalendarHeart className="w-6 h-6 text-white" />
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold text-white">Activités Voyageurs</h1>
                </div>
                <p className="text-white/80 text-sm max-w-lg leading-relaxed">
                  Organisez et rejoignez des activités avec d'autres voyageurs. Randos, roadtrips, soirées... trouvez votre prochaine aventure !
                </p>
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <Flame className="w-4 h-4 text-amber-400" />
                    <span>{totalCount} activité{totalCount > 1 ? 's' : ''} disponible{totalCount > 1 ? 's' : ''}</span>
                  </div>
                </div>
              </motion.div>
              <div className="flex items-center gap-3">
                {user.uid === '1' && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.15 }}
                    onClick={async () => {
                      const confirmCreate = window.confirm('Créer 15 activités de démonstration ?');
                      if (!confirmCreate) return;

                      try {
                        const count = await seedActivities(user.uid, userInfo.user.username);
                        await fetchActivities(currentFilters);
                        alert(`✅ ${count} activités créées avec succès !`);
                      } catch (error) {
                        console.error('Error seeding activities:', error);
                        alert('❌ Erreur: ' + (error as Error).message);
                      }
                    }}
                    className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl transition-colors text-sm font-semibold shadow-lg shadow-amber-500/20"
                  >
                    <Sparkles className="w-4 h-4" />
                    Créer activités démo
                  </motion.button>
                )}
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  onClick={() => setShowCreateForm(true)}
                  className="hidden md:flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 text-teal-700 rounded-xl transition-colors text-sm font-semibold shadow-lg shadow-black/10"
                >
                  <Plus className="w-5 h-5" />
                  Proposer une activité
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 pt-24 sm:pt-8 pb-24 sm:pb-16">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-3">
            <div className="sm:hidden w-10 h-10 bg-teal-50 dark:bg-teal-900/20 rounded-xl flex items-center justify-center">
              <CalendarHeart className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white sm:hidden">Activités Voyageurs</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                {totalCount} activité{totalCount > 1 ? 's' : ''} disponible{totalCount > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex md:hidden items-center justify-center gap-2 px-5 py-3 sm:py-2.5 bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white rounded-xl transition-colors text-sm font-semibold shadow-sm touch-manipulation"
          >
            <Plus className="w-4.5 h-4.5" />
            Proposer une activité
          </button>
        </div>

        <div className="mb-4 sm:mb-6">
          <ActivityFilters onFilterChange={handleFilterChange} />
        </div>

        <ActivityList
          activities={activities}
          isLoading={isLoading}
          currentUserId={user.uid}
          onOpenActivity={(activity) => setSelectedActivity(activity)}
          onLike={handleLike}
          onFavorite={handleFavorite}
          likedIds={likedIds}
          favoritedIds={favoritedIds}
        />

        {isLoadingMore && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-teal-600" />
          </div>
        )}

        <div ref={observerRef} className="h-10" />
      </main>

      <Footer />

      {showCreateForm && (
        <CreateActivityForm
          onSubmit={handleCreateActivity}
          onClose={() => setShowCreateForm(false)}
          isSubmitting={isSubmitting}
        />
      )}

      {selectedActivity && (
        <ActivityDetailModal
          activity={selectedActivity}
          isOpen={true}
          onClose={() => setSelectedActivity(null)}
          currentUserId={user.uid}
          currentUsername={userInfo?.user.username}
          onJoin={handleJoin}
          onLeave={handleLeave}
          onComment={handleComment}
          onReply={handleReply}
          onLike={handleLike}
          onFavorite={handleFavorite}
          onDelete={(user.uid === selectedActivity.userId || isAdmin) ? handleDelete : undefined}
          isJoining={isJoining}
          isAdmin={isAdmin}
          isLiked={likedIds.has(selectedActivity.id)}
          isFavorited={favoritedIds.has(selectedActivity.id)}
        />
      )}
    </div>
  );
}
